import { Datapoint } from "@/types/app/dataset";
import { formatValueForCSV } from "./csv";
import { ChatMessage } from "@/services/promptEditor.service";

const getDatasetCSVHeaders = (datapoints: Datapoint[]) => {
  const inputHeaders: Set<string> = new Set();
  const targetHeaders: Set<string> = new Set();
  const hasMessages = datapoints.some((d) => d.messages && Array.isArray(d.messages) && d.messages.length > 0);

  datapoints.forEach((d) => {
    // First add the keys for inputs
    Object.keys(d.inputs).forEach((key) => {
      inputHeaders.add(key);
    });

    // Then add the keys for target
    if (d.target && typeof d.target === "object") {
      Object.keys(d.target).forEach((key) => {
        targetHeaders.add(key);
      });
    }
  });

  return { inputHeaders: Array.from(inputHeaders), targetHeaders: Array.from(targetHeaders), hasMessages };
};

const convertChatMessageToKeyValue = (message: ChatMessage): Record<string, any> => {
  const output: Record<string, any> = {};

  if (message.role) {
    output.role = message.role;
  }
  if (message.content) {
    output.content = message.content;
  }
  if (message.name) {
    output.name = message.name;
  }
  if (message.tool_call_id) {
    output.tool_call_id = message.tool_call_id;
  }
  if (message.tool_calls) {
    output.tool_calls = message.tool_calls;
  }
  return output;
};

const formatDatapointMessagesForCSV = (datapoint: Datapoint): string => {
  if (!datapoint.messages || !Array.isArray(datapoint.messages)) {
    return "";
  }

  const messages = datapoint.messages
    .map((msg) => {
      return JSON.stringify(convertChatMessageToKeyValue(msg));
    })
    .join(", ");

  if (messages.length === 0) {
    return "";
  }

  // Escape quotes only once, add square brackets to make it an array
  return `"[${messages.replace(/"/g, '""')}]"`;
};

const formatDatapointInputForCSV = (datapoint: Datapoint, key: string): string => {
  if (!(key in datapoint.inputs)) {
    console.error(`Key ${key} not found in inputs object`);
    return "";
  }

  return formatValueForCSV(datapoint.inputs[key]);
};

const formatDatapointTargetForCSV = (datapoint: Datapoint, key: string): string => {
  if (!datapoint.target) {
    console.error("Target object missing");
    return "";
  }

  if (!(key in datapoint.target)) {
    console.error(`Key ${key} not found in target object`);
    return "";
  }

  return formatValueForCSV(datapoint.target[key]);
};

const getDatapointCSVRow = (
  datapoint: Datapoint,
  inputHeaders: string[],
  targetHeaders: string[],
  hasMessages: boolean,
): string[] => {
  const row: string[] = [];

  inputHeaders.forEach((key) => {
    row.push(formatDatapointInputForCSV(datapoint, key));
  });

  if (hasMessages) {
    row.push(formatDatapointMessagesForCSV(datapoint));
  }

  targetHeaders.forEach((key) => {
    row.push(formatDatapointTargetForCSV(datapoint, key));
  });

  return row;
};

export const createCSVBlobFromDatapoints = async (datapoints: Datapoint[]) => {
  const { inputHeaders, targetHeaders, hasMessages } = getDatasetCSVHeaders(datapoints);

  const formattedCSVLines = datapoints.map((d) => {
    const row = getDatapointCSVRow(d, inputHeaders, targetHeaders, hasMessages);
    return row.join(",");
  });

  if (targetHeaders && targetHeaders.length === 1 && targetHeaders.includes("output")) {
    targetHeaders.pop();
    targetHeaders.push("target");
  }

  const orderedHeaders = [...inputHeaders, ...(hasMessages ? ["message"] : []), ...targetHeaders];

  const csvData = [orderedHeaders.join(",")].concat(formattedCSVLines).join("\n");
  return new Blob([csvData], { type: "text/csv" });
};
